/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
    MdExpandMore,
    MdExpandLess,
    MdFolder,
    MdFolderOpen,
    MdArticle,
    MdSearch,
} from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
    Typography,
    Card,
    CardContent,
    CardActionArea,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    TextField,
    InputAdornment,
} from '@material-ui/core';

import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';

import history from '~/services/history';
import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';

export default function PublicArticles() {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [allArticles, setAllArticles] = useState([]);
    const [uncategorizedArticles, setUncategorizedArticles] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            toast.error(message);
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            }
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadArticles() {
        setIsLoading(true);
        try {
            const categoriesResponse = await api.get(
                `articles/public/categories`
            );
            setCategories(categoriesResponse.data || []);

            const allArticlesResponse = await api.get(`articles/public`);
            setAllArticles(allArticlesResponse.data || []);

            const uncategorizedResponse = await api.get(
                `articles/public?uncategorized=true`
            );
            setUncategorizedArticles(uncategorizedResponse.data || []);
        } catch (error) {
            requestError(error);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        loadArticles();
    }, []);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setSearchResults([]);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = allArticles.filter(article => {
            const titleMatch = article.title.toLowerCase().includes(term);
            const contentMatch =
                article.content && article.content.toLowerCase().includes(term);
            return titleMatch || contentMatch;
        });
        setSearchResults(filtered);
    }, [searchTerm, allArticles]);

    function toggleCategory(categoryId) {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    }

    function handleArticleClick(articleId) {
        history.push(`/knowledge-base/${articleId}`);
    }

    function handleSearchChange(event) {
        setSearchTerm(event.target.value);
    }

    function renderArticleItem(article) {
        return (
            <ListItem
                key={article.id}
                button
                onClick={() => handleArticleClick(article.id)}
                style={{ paddingLeft: '24px' }}
            >
                <ListItemIcon>
                    <MdArticle size={20} color="#1976d2" />
                </ListItemIcon>
                <ListItemText primary={article.title} />
            </ListItem>
        );
    }

    function renderSearchResultCard(article) {
        return (
            <Card
                key={article.id}
                style={{
                    marginBottom: '10px',
                    cursor: 'pointer',
                }}
            >
                <CardActionArea onClick={() => handleArticleClick(article.id)}>
                    <CardContent>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                            }}
                        >
                            <MdArticle size={24} color="#1976d2" />
                            <div style={{ flex: 1 }}>
                                <Typography variant="h6">
                                    {article.title}
                                </Typography>
                                {article.category && (
                                    <Chip
                                        size="small"
                                        label={article.category.name}
                                        style={{ marginTop: '5px' }}
                                    />
                                )}
                            </div>
                        </div>
                    </CardContent>
                </CardActionArea>
            </Card>
        );
    }

    function renderSubcategory(subcategory) {
        if (!subcategory.articles || subcategory.articles.length === 0) {
            return null;
        }

        const isExpanded = expandedCategories[`sub_${subcategory.id}`];

        return (
            <div key={subcategory.id}>
                <ListItem
                    button
                    onClick={() => toggleCategory(`sub_${subcategory.id}`)}
                    style={{ paddingLeft: '16px' }}
                >
                    <ListItemIcon>
                        <MdFolder size={18} color="#666" />
                    </ListItemIcon>
                    <ListItemText primary={subcategory.name} />
                    <Chip
                        size="small"
                        label={subcategory.articles.length}
                        style={{ marginRight: '8px' }}
                    />
                    {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {subcategory.articles.map(article =>
                            renderArticleItem(article)
                        )}
                    </List>
                </Collapse>
            </div>
        );
    }

    function renderCategoryCard(category) {
        const hasArticles =
            (category.articles && category.articles.length > 0) ||
            (category.subcategories &&
                category.subcategories.some(
                    sub => sub.articles && sub.articles.length > 0
                ));

        if (!hasArticles) {
            return null;
        }

        const totalArticles =
            (category.articles ? category.articles.length : 0) +
            (category.subcategories
                ? category.subcategories.reduce(
                      (acc, sub) =>
                          acc + (sub.articles ? sub.articles.length : 0),
                      0
                  )
                : 0);

        const isExpanded = expandedCategories[category.id];

        return (
            <Card
                key={category.id}
                style={{
                    minWidth: '300px',
                    maxWidth: '400px',
                    flex: '1 1 300px',
                }}
            >
                <CardActionArea onClick={() => toggleCategory(category.id)}>
                    <CardContent>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}
                            >
                                {isExpanded ? (
                                    <MdFolderOpen size={28} color="#1976d2" />
                                ) : (
                                    <MdFolder size={28} color="#666" />
                                )}
                                <Typography variant="h6">
                                    {category.name}
                                </Typography>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Chip
                                    size="small"
                                    label={totalArticles}
                                    color="primary"
                                />
                                {isExpanded ? (
                                    <MdExpandLess />
                                ) : (
                                    <MdExpandMore />
                                )}
                            </div>
                        </div>
                    </CardContent>
                </CardActionArea>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {category.articles &&
                            category.articles.map(article =>
                                renderArticleItem(article)
                            )}
                        {category.subcategories &&
                            category.subcategories.map(sub =>
                                renderSubcategory(sub)
                            )}
                    </List>
                </Collapse>
            </Card>
        );
    }

    function renderUncategorizedCard() {
        if (uncategorizedArticles.length === 0) return null;

        const isExpanded = expandedCategories.uncategorized;

        return (
            <Card
                style={{
                    minWidth: '300px',
                    maxWidth: '400px',
                    flex: '1 1 300px',
                }}
            >
                <CardActionArea onClick={() => toggleCategory('uncategorized')}>
                    <CardContent>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}
                            >
                                <MdFolder size={28} color="#666" />
                                <Typography variant="h6">
                                    <LocaleMessage msg="page.knowledge_base.uncategorized" />
                                </Typography>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Chip
                                    size="small"
                                    label={uncategorizedArticles.length}
                                    color="default"
                                />
                                {isExpanded ? (
                                    <MdExpandLess />
                                ) : (
                                    <MdExpandMore />
                                )}
                            </div>
                        </div>
                    </CardContent>
                </CardActionArea>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {uncategorizedArticles.map(article =>
                            renderArticleItem(article)
                        )}
                    </List>
                </Collapse>
            </Card>
        );
    }

    function renderSearchResults() {
        if (searchResults.length === 0) {
            return (
                <div
                    style={{
                        width: '100%',
                        textAlign: 'center',
                        padding: '50px',
                        color: '#666',
                    }}
                >
                    <MdSearch size={64} />
                    <Typography variant="h6" style={{ marginTop: '20px' }}>
                        <LocaleMessage msg="page.knowledge_base.no_results" />
                    </Typography>
                </div>
            );
        }

        return (
            <div style={{ width: '100%' }}>
                <Typography
                    variant="subtitle1"
                    style={{ marginBottom: '15px', color: '#666' }}
                >
                    {searchResults.length}{' '}
                    <LocaleMessage msg="page.knowledge_base.results_found" />
                </Typography>
                {searchResults.map(article => renderSearchResultCard(article))}
            </div>
        );
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.knowledge_base.title" />}
            breadcrumbs={[
                {
                    url: '/',
                    title: <LocaleMessage msg="breadcrumbs.home" />,
                },
            ]}
            loading={isLoading}
        >
            <div style={{ padding: '20px' }}>
                {/* Search Bar */}
                <div style={{ marginBottom: '20px' }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Pesquisar artigos..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MdSearch size={24} color="#666" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                {/* Content */}
                {searchTerm.trim() !== '' ? (
                    renderSearchResults()
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '20px',
                            alignItems: 'flex-start',
                        }}
                    >
                        {categories.map(category =>
                            renderCategoryCard(category)
                        )}
                        {renderUncategorizedCard()}

                        {categories.length === 0 &&
                            uncategorizedArticles.length === 0 &&
                            !isLoading && (
                                <div
                                    style={{
                                        width: '100%',
                                        textAlign: 'center',
                                        padding: '50px',
                                        color: '#666',
                                    }}
                                >
                                    <MdArticle size={64} />
                                    <Typography
                                        variant="h6"
                                        style={{ marginTop: '20px' }}
                                    >
                                        <LocaleMessage msg="page.knowledge_base.empty" />
                                    </Typography>
                                </div>
                            )}
                    </div>
                )}
            </div>
        </PageContent>
    );
}
